/* ============================================================================
 *  Dev GTM tracking layer (Revco / Horizon-based custom theme)
 *  ----------------------------------------------------------------------------
 *  Pushes GA4-style ecommerce + interaction events into window.dataLayer so a
 *  Google Tag Manager container can pick them up. While the GTM container ID
 *  in layout/theme.liquid is the placeholder `GTM-XXXXXXX`, no real network
 *  call is made to googletagmanager.com; only dataLayer pushes happen.
 *
 *  ▸ WHERE TO PUT THE REAL GTM ID
 *    layout/theme.liquid → look for the `assign gtm_id = 'GTM-XXXXXXX'` line
 *    near the top of <head>. Replacing it switches the page from QA mode to
 *    real GTM mode automatically — no code change here required.
 *
 *  ▸ HOW TO TOGGLE QA CONSOLE LOGS
 *    Three ways, in priority order:
 *      1. Run in the browser console:  devGtm.setDebug(true|false)
 *         (persists across page loads via localStorage)
 *      2. Add `?gtm_debug=1` (force on) or `?gtm_off=1` (force off) to any URL
 *      3. Edit `debug: true` → `false` in window.devGtmConfig inside theme.liquid
 *    Logs are namespaced with the [GTM] prefix so they're easy to filter.
 *
 *  ▸ HOW TO HARD-DISABLE THE WHOLE LAYER
 *    Set `enabled: false` in window.devGtmConfig (theme.liquid) — both the
 *    GTM bootstrap and this tracker become no-ops.
 *
 *  ▸ NEW EVENTS
 *    Use `devGtm.push(eventName, paramsObject)`. The helper auto-resets the
 *    `ecommerce` slot per GA4 guidance when an ecommerce payload is present.
 * ========================================================================== */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  //  Config + bootstrap
  // -------------------------------------------------------------------------
  var cfg = window.devGtmConfig || {};
  if (cfg.enabled === false) return;

  window.dataLayer = window.dataLayer || [];

  var DEBUG_LS_KEY = 'devGtmDebug';
  var debug = resolveDebugFlag();

  function resolveDebugFlag() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('gtm_off') === '1') return false;
      if (params.get('gtm_debug') === '1') return true;
      var stored = window.localStorage.getItem(DEBUG_LS_KEY);
      if (stored === '1') return true;
      if (stored === '0') return false;
    } catch (_) { /* localStorage / URL access blocked — ignore */ }
    return cfg.debug !== false; // default ON during QA
  }

  function log(label, payload) {
    if (!debug) return;
    if (payload === undefined) {
      console.log('%c[GTM]%c ' + label, 'color:#213D8E;font-weight:600', 'color:inherit');
    } else {
      console.log('%c[GTM]%c ' + label, 'color:#213D8E;font-weight:600', 'color:inherit', payload);
    }
  }

  function warn(label, err) {
    if (!debug) return;
    console.warn('%c[GTM]%c ' + label, 'color:#a05a00;font-weight:600', 'color:inherit', err);
  }

  // -------------------------------------------------------------------------
  //  Public API — window.devGtm
  // -------------------------------------------------------------------------
  var api = {
    push: push,
    setDebug: function (on) {
      debug = !!on;
      try { window.localStorage.setItem(DEBUG_LS_KEY, on ? '1' : '0'); } catch (_) {}
      log('debug ' + (on ? 'enabled' : 'disabled'));
    },
    getDebug: function () { return debug; },
    config: cfg
  };
  window.devGtm = api;

  function push(eventName, params) {
    var payload = Object.assign({ event: eventName }, params || {});
    // GA4 best practice: clear `ecommerce` before every new ecommerce payload
    // so leftover items from the previous event can't leak into the next.
    if (payload.ecommerce) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(payload);
    log(eventName, payload);
  }

  // -------------------------------------------------------------------------
  //  Helpers
  // -------------------------------------------------------------------------
  function moneyToNumber(text) {
    if (typeof text !== 'string') return null;
    var cleaned = text.replace(/[^0-9.,-]/g, '').replace(/,(?=\d{3}\b)/g, '');
    // Convert remaining comma decimal separator (e.g. "1.299,00") to dot
    var lastComma = cleaned.lastIndexOf(',');
    var lastDot = cleaned.lastIndexOf('.');
    if (lastComma > lastDot) cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    else cleaned = cleaned.replace(/,/g, '');
    var n = parseFloat(cleaned);
    return isFinite(n) ? n : null;
  }

  function pageType() {
    try {
      if (window.Theme && window.Theme.template && window.Theme.template.name) {
        return String(window.Theme.template.name);
      }
    } catch (_) {}
    return 'unknown';
  }

  function currency() {
    if (cfg.currency) return cfg.currency;
    var meta = document.querySelector('meta[property="og:price:currency"]');
    if (meta && meta.content) return meta.content;
    return 'USD';
  }

  function handleFromUrl(url) {
    if (!url) return '';
    var m = String(url).match(/\/products\/([a-z0-9-_]+)/i);
    return m ? m[1] : '';
  }

  // Build a GA4 `item` object from a `.dev-product-card` DOM node.
  function itemFromCard(card, extra) {
    if (!card) return null;
    var titleEl = card.querySelector('.dev-product-card__title a, .dev-product-card__title');
    var linkEl = card.querySelector('a[href*="/products/"]');
    var priceEl = card.querySelector('.dev-product-card__price-current');
    var badgeEl = card.querySelector('.dev-product-card__badge');
    var subtypeEl = card.querySelector('.dev-product-card__subtype');
    var href = linkEl ? linkEl.getAttribute('href') : '';
    var item = {
      item_id: handleFromUrl(href) || (titleEl ? (titleEl.textContent || '').trim() : ''),
      item_name: titleEl ? (titleEl.textContent || '').trim() : '',
      price: priceEl ? moneyToNumber(priceEl.textContent) : null,
      item_variant: subtypeEl ? (subtypeEl.textContent || '').trim() : '',
      item_brand: badgeEl ? (badgeEl.textContent || '').trim() : '',
      quantity: 1
    };
    if (extra) Object.assign(item, extra);
    Object.keys(item).forEach(function (k) {
      if (item[k] === null || item[k] === '' || item[k] === undefined) delete item[k];
    });
    return item;
  }

  // Derive a list name (item_list_name) for cards inside a section.
  function listNameFor(card) {
    var section = card.closest('[id^="shopify-section-"], section, [data-section-id]');
    if (!section) return 'unknown';
    var devClass = '';
    if (section.classList) {
      for (var i = 0; i < section.classList.length; i++) {
        var c = section.classList[i];
        if (c.indexOf('dev-') === 0 && c.indexOf('-section') === -1) { devClass = c; break; }
      }
    }
    if (devClass) return devClass.replace(/^dev-/, '');
    if (section.id) return section.id.replace(/^shopify-section-/, '');
    return 'unknown';
  }

  // -------------------------------------------------------------------------
  //  page_view  +  view_item  (PDP-only context provided by Liquid)
  // -------------------------------------------------------------------------
  function emitPageView() {
    push('page_view', {
      page_type: pageType(),
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
      page_title: document.title
    });
  }

  function emitViewItem() {
    var p = window.devGtmProduct;
    if (!p || !p.id) return;
    push('view_item', {
      ecommerce: {
        currency: currency(),
        value: p.price || 0,
        items: [{
          item_id: String(p.id),
          item_name: p.title || '',
          item_brand: p.vendor || '',
          item_category: p.type || '',
          item_variant: p.variantTitle || '',
          price: p.price || 0,
          quantity: 1
        }]
      }
    });
  }

  // -------------------------------------------------------------------------
  //  view_item_list — fires once per card when scrolled into view
  // -------------------------------------------------------------------------
  function observeCards() {
    if (!('IntersectionObserver' in window)) return;
    var seen = new WeakSet();
    var io = new IntersectionObserver(function (entries) {
      var grouped = {};
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;
        if (seen.has(card)) return;
        seen.add(card);
        io.unobserve(card);
        var list = listNameFor(card);
        (grouped[list] = grouped[list] || []).push(itemFromCard(card));
      });
      Object.keys(grouped).forEach(function (list) {
        var items = grouped[list].filter(Boolean);
        if (!items.length) return;
        push('view_item_list', {
          ecommerce: {
            currency: currency(),
            item_list_name: list,
            items: items
          }
        });
      });
    }, { threshold: 0.4 });

    function scan(root) {
      (root || document).querySelectorAll('.dev-product-card').forEach(function (card) {
        if (seen.has(card)) return;
        io.observe(card);
      });
    }

    scan(document);

    // The collection grid re-renders on filter / pagination — re-scan when
    // sections morph in new cards (Section Rendering API path).
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) return;
          if (node.matches && node.matches('.dev-product-card')) io.observe(node);
          else scan(node);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // -------------------------------------------------------------------------
  //  select_item — click on a product card
  // -------------------------------------------------------------------------
  function bindSelectItem() {
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      // Ignore clicks on the in-card "add to cart" form — those are handled
      // by the add_to_cart fetch interceptor.
      if (target.closest('.dev-product-card__form, .dev-product-card__cart-btn')) return;
      var card = target.closest('.dev-product-card');
      if (!card) return;
      var item = itemFromCard(card);
      if (!item) return;
      push('select_item', {
        ecommerce: {
          item_list_name: listNameFor(card),
          items: [item]
        }
      });
    }, true);
  }

  // -------------------------------------------------------------------------
  //  add_to_cart / remove_from_cart — global fetch interceptor
  //  ----------------------------------------------------------
  //  Every cart mutation in the theme goes through /cart/add.js,
  //  /cart/change.js or /cart/update.js. Wrapping `fetch` once here
  //  captures product-card quick-adds, PDP submits, drawer recs and
  //  drawer qty/remove clicks in a single place.
  // -------------------------------------------------------------------------
  function isCartPath(url, suffix) {
    if (typeof url !== 'string') return false;
    var path = url.split('?')[0].split('#')[0];
    return new RegExp('/cart/' + suffix + '(\\.js)?$').test(path);
  }

  function bodyToObject(body) {
    if (!body) return {};
    if (body instanceof FormData) {
      var out = {};
      body.forEach(function (val, key) { out[key] = val; });
      return out;
    }
    if (typeof body === 'string') {
      try { return JSON.parse(body); }
      catch (_) {
        try {
          var p = new URLSearchParams(body); var o = {};
          p.forEach(function (v, k) { o[k] = v; });
          return o;
        } catch (__) { return {}; }
      }
    }
    if (typeof body === 'object') {
      try { return JSON.parse(JSON.stringify(body)); } catch (_) { return {}; }
    }
    return {};
  }

  function itemFromCartLine(line) {
    if (!line) return null;
    return {
      item_id: String(line.product_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_variant: line.variant_title || '',
      item_brand: line.vendor || '',
      item_category: line.product_type || '',
      price: typeof line.final_price === 'number'
        ? line.final_price / 100
        : (typeof line.price === 'number' ? line.price / 100 : null),
      quantity: line.quantity || 1
    };
  }

  // Cached cart snapshot — populated from any cart-mutation response
  // (Shopify returns full cart on /cart/add.js & /cart/change.js when
  // `sections` is in the body, else only the modified line) and from a
  // background /cart.js refresh. Used to make `view_cart` and
  // `begin_checkout` synchronous so they survive navigation away.
  var cartCache = null;
  function refreshCartCache() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) cartCache = cart; return cart; })
      .catch(function () { return null; });
  }

  function wrapFetch() {
    if (typeof window.fetch !== 'function') return;
    if (window.fetch.__devGtmWrapped) return;
    var origFetch = window.fetch.bind(window);

    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var isAdd = method === 'POST' && isCartPath(url, 'add');
      var isChange = method === 'POST' && (isCartPath(url, 'change') || isCartPath(url, 'update'));

      // Capture pre-state for change/remove (we need the line BEFORE the mutation
      // to know what was removed and at what quantity).
      var requestedBody = isAdd || isChange
        ? bodyToObject(init && init.body)
        : null;

      var preSnapshot = isChange
        ? origFetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; })
        : Promise.resolve(null);

      return preSnapshot.then(function (preCart) {
        return origFetch(input, init).then(function (response) {
          if (!isAdd && !isChange) return response;
          // Clone so the original caller still gets a fresh body to read.
          var clone = response.clone();
          clone.json().then(function (data) {
            try {
              if (isAdd) handleAddResponse(data, requestedBody);
              if (isChange) handleChangeResponse(data, requestedBody, preCart);
            } catch (e) { warn('cart event handler error', e); }
          }).catch(function () { /* response wasn't JSON — ignore */ });
          return response;
        });
      });
    };

    wrapped.__devGtmWrapped = true;
    window.fetch = wrapped;
  }

  function handleAddResponse(data, requestedBody) {
    // /cart/add.js returns either the added line directly or `{ items: [...] }`
    // if multiple lines were added in one call.
    var lines = [];
    if (Array.isArray(data && data.items)) lines = data.items;
    else if (data && data.id) lines = [data];

    // Refresh cart cache in background — used by view_cart / begin_checkout.
    refreshCartCache();

    if (!lines.length) return;

    var items = lines.map(itemFromCartLine).filter(Boolean);
    var value = items.reduce(function (sum, it) {
      return sum + (it.price || 0) * (it.quantity || 1);
    }, 0);

    push('add_to_cart', {
      ecommerce: {
        currency: currency(),
        value: Number(value.toFixed(2)),
        items: items
      },
      source: detectAddSource(requestedBody)
    });
  }

  function detectAddSource(body) {
    // Heuristic — the dev-cart-drawer recs button sets `sections=<id>`. The
    // PDP product-form does too but its formdata also has `properties` keys.
    if (!body) return 'unknown';
    if (window.location.pathname.indexOf('/products/') === 0) return 'pdp';
    if (body.sections && body.sections.indexOf('dev-cart-drawer') !== -1) return 'cart_drawer_recommendation';
    return 'product_card';
  }

  function handleChangeResponse(data, requestedBody, preCart) {
    // /cart/change.js with quantity=0 → remove_from_cart
    // /cart/change.js with quantity>previous → add (extra qty) — we don't
    //   double-emit add_to_cart for these (drawer +/- buttons); GA4 prefers
    //   a single update_quantity signal instead.

    // Refresh cart cache in background — used by view_cart / begin_checkout.
    refreshCartCache();

    if (!requestedBody || !preCart) return;
    var line = requestedBody.line ? parseInt(requestedBody.line, 10) : null;
    var id = requestedBody.id || null;
    var nextQty = parseInt(requestedBody.quantity, 10);

    var preLine = null;
    if (line && preCart.items && preCart.items[line - 1]) preLine = preCart.items[line - 1];
    else if (id && preCart.items) preLine = preCart.items.find(function (l) { return String(l.key) === String(id) || String(l.id) === String(id); });

    if (!preLine) return;

    if (nextQty === 0) {
      var item = itemFromCartLine(preLine);
      item.quantity = preLine.quantity;
      var removeValue = (item.price || 0) * (item.quantity || 1);
      push('remove_from_cart', {
        ecommerce: {
          currency: currency(),
          value: Number(removeValue.toFixed(2)),
          items: [item]
        }
      });
      return;
    }

    var delta = nextQty - preLine.quantity;
    if (delta === 0) return;
    var changedItem = itemFromCartLine(preLine);
    changedItem.quantity = Math.abs(delta);
    var changeValue = (changedItem.price || 0) * Math.abs(delta);
    push(delta > 0 ? 'add_to_cart' : 'remove_from_cart', {
      ecommerce: {
        currency: currency(),
        value: Number(changeValue.toFixed(2)),
        items: [changedItem]
      },
      source: 'cart_drawer_quantity'
    });
  }

  // -------------------------------------------------------------------------
  //  view_cart  +  begin_checkout
  // -------------------------------------------------------------------------
  function pushCartEvent(eventName, cart) {
    if (!cart) return;
    var items = (cart.items || []).map(itemFromCartLine).filter(Boolean);
    push(eventName, {
      ecommerce: {
        currency: cart.currency || currency(),
        value: (cart.total_price || 0) / 100,
        items: items
      }
    });
  }

  function bindCartDrawerEvents() {
    var lastViewCartAt = 0;
    function emitViewCart() {
      // De-bounce: header click also fires the custom event 100ms later.
      if (Date.now() - lastViewCartAt < 400) return;
      lastViewCartAt = Date.now();
      // Prefer cached cart so the event lands immediately; refresh in background.
      if (cartCache) pushCartEvent('view_cart', cartCache);
      refreshCartCache().then(function (cart) {
        if (cart && !cartCache) pushCartEvent('view_cart', cart);
      });
    }
    window.addEventListener('dev-cart:open', emitViewCart);
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-dev-cart-open]')) emitViewCart();
    });
  }

  function bindBeginCheckout() {
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!(t instanceof Element)) return;
      var link = t.closest('a[href*="/checkout"], .dev-cart-drawer__checkout');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href && href.indexOf('/checkout') === -1) return;
      // Push synchronously from cache — by the time the page navigates,
      // dataLayer already contains the begin_checkout event, so GTM can
      // forward it before unload.
      if (cartCache) {
        pushCartEvent('begin_checkout', cartCache);
        return;
      }
      // Cache miss — fall back to async fetch (best-effort; may race nav).
      refreshCartCache().then(function (cart) { pushCartEvent('begin_checkout', cart); });
    }, true);
  }

  // -------------------------------------------------------------------------
  //  search
  // -------------------------------------------------------------------------
  function bindSearch() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/search') === -1) return;
      var input = form.querySelector('input[name="q"]');
      var term = input ? input.value : '';
      if (!term) return;
      push('search', { search_term: term });
    }, true);

    // If the page itself is /search?q=… fire a `view_search_results` on load
    if (window.location.pathname.indexOf('/search') === 0) {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('q');
      if (q) push('view_search_results', { search_term: q });
    }
  }

  // -------------------------------------------------------------------------
  //  Quiz (Helmet Finder)
  // -------------------------------------------------------------------------
  function bindQuiz() {
    var quizRoot = document.querySelector('[data-dev-quiz-modal]');
    if (!quizRoot) return;

    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!(t instanceof Element)) return;

      if (t.closest('[data-dev-quiz-open]')) {
        push('quiz_open', { source: contextLabelFor(t) });
        return;
      }
      if (t.closest('[data-dev-quiz-start]')) {
        push('quiz_start', {});
        return;
      }
      var answer = t.closest('[data-dev-quiz-answer]');
      if (answer) {
        var question = answer.closest('[data-dev-quiz-question]');
        push('quiz_answer', {
          question_index: question ? Number(question.getAttribute('data-dev-quiz-question')) : null,
          answer_target: answer.getAttribute('data-target') || '',
          answer_label: (answer.textContent || '').trim()
        });
        return;
      }
      if (t.closest('[data-dev-quiz-restart]')) {
        push('quiz_restart', {});
        return;
      }
      var resultLink = t.closest('[data-dev-quiz-result] a, [data-dev-quiz-result] button');
      if (resultLink) {
        var resultBlock = resultLink.closest('[data-dev-quiz-result]');
        push('quiz_result_click', {
          result_key: resultBlock ? resultBlock.getAttribute('data-dev-quiz-result') : '',
          link_href: resultLink.getAttribute('href') || ''
        });
      }
    });

    // quiz_complete — fired when the results panel transitions from hidden→shown.
    var resultsPanel = quizRoot.querySelector('[data-dev-quiz-results]');
    if (resultsPanel) {
      var wasHidden = resultsPanel.hidden;
      new MutationObserver(function () {
        if (wasHidden && !resultsPanel.hidden) {
          var visible = resultsPanel.querySelector('[data-dev-quiz-result]:not([hidden])');
          push('quiz_complete', {
            result_key: visible ? visible.getAttribute('data-dev-quiz-result') : ''
          });
        }
        wasHidden = resultsPanel.hidden;
      }).observe(resultsPanel, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  function contextLabelFor(node) {
    if (!node) return 'unknown';
    if (node.closest('.dev-header')) return 'header';
    if (node.closest('.dev-footer')) return 'footer';
    if (node.closest('.dev-hero')) return 'hero';
    if (node.closest('.dev-industries')) return 'industries_card';
    if (node.closest('section')) {
      var s = node.closest('section');
      for (var i = 0; i < s.classList.length; i++) {
        if (s.classList[i].indexOf('dev-') === 0) return s.classList[i];
      }
    }
    return 'unknown';
  }

  // -------------------------------------------------------------------------
  //  Forms: newsletter, contact, custom quote, generic
  // -------------------------------------------------------------------------
  function bindForms() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';

      // Cart-add forms are tracked via the fetch interceptor, skip here.
      if (/\/cart\/add(\.js)?$/.test(action.split('?')[0])) return;
      // Search forms are tracked separately.
      if (action.indexOf('/search') !== -1) return;

      var classList = form.className || '';
      var formType = 'other';
      if (classList.indexOf('dev-footer__newsletter-form') !== -1) formType = 'newsletter';
      else if (classList.indexOf('dev-custom-quote__form') !== -1) formType = 'custom_quote';
      else if (action.indexOf('/contact') !== -1) formType = 'contact';
      else if (action.indexOf('/account') !== -1) formType = 'account';

      var email = form.querySelector('input[type="email"]');
      var name = form.querySelector('input[name="contact[name]"], input[name="name"]');
      var phone = form.querySelector('input[name="contact[phone]"], input[type="tel"]');

      push('form_submit', {
        form_type: formType,
        form_id: form.getAttribute('id') || '',
        form_action: action,
        has_email: !!(email && email.value),
        has_phone: !!(phone && phone.value),
        has_name: !!(name && name.value)
      });

      if (formType === 'newsletter') {
        push('newsletter_signup', { method: 'footer_form' });
      } else if (formType === 'custom_quote') {
        var model = form.querySelector('select[name="contact[helmet_model]"]');
        var quantity = form.querySelector('input[name="contact[quantity]"]');
        push('generate_lead', {
          lead_type: 'custom_quote',
          helmet_model: model ? model.value : '',
          quantity: quantity ? Number(quantity.value) || null : null
        });
      } else if (formType === 'contact') {
        push('generate_lead', { lead_type: 'contact' });
      }
    }, true);

    // Some forms render server-side success states (e.g. dev-custom-quote shows
    // a success block on reload after posted_successfully). Detect that block
    // and fire a confirmation event on the post-redirect page view.
    var quoteSuccess = document.querySelector('.dev-custom-quote__alert--success');
    if (quoteSuccess) {
      push('form_submit_success', { form_type: 'custom_quote' });
      push('generate_lead', { lead_type: 'custom_quote', stage: 'confirmed' });
    }
  }

  // -------------------------------------------------------------------------
  //  Navigation, scroll-to-top, outbound, video
  // -------------------------------------------------------------------------
  function bindNavigation() {
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!(t instanceof Element)) return;

      var navLink = t.closest('.dev-header__nav-link, .dev-header__drawer-link');
      if (navLink) {
        push('navigation_click', {
          link_text: (navLink.textContent || '').trim(),
          link_url: navLink.getAttribute('href') || '',
          location: 'header'
        });
        return;
      }
      var footerLink = t.closest('.dev-footer a[href]');
      if (footerLink) {
        push('navigation_click', {
          link_text: (footerLink.textContent || '').trim(),
          link_url: footerLink.getAttribute('href') || '',
          location: 'footer'
        });
        return;
      }
      if (t.closest('.dev-scroll-to-top, [data-dev-scroll-to-top]')) {
        push('scroll_to_top_click', {});
        return;
      }
      var external = t.closest('a[href^="http"]');
      if (external) {
        var href = external.getAttribute('href') || '';
        try {
          var u = new URL(href, window.location.href);
          if (u.hostname && u.hostname !== window.location.hostname) {
            push('outbound_click', {
              link_url: href,
              link_domain: u.hostname,
              link_text: (external.textContent || '').trim()
            });
          }
        } catch (_) { /* malformed URL — ignore */ }
      }
    });
  }

  function bindVideos() {
    document.addEventListener('play', function (event) {
      var v = event.target;
      if (!(v instanceof HTMLVideoElement)) return;
      var src = v.currentSrc || v.src || '';
      push('video_play', {
        video_src: src,
        video_duration: isFinite(v.duration) ? Math.round(v.duration) : null
      });
    }, true);
  }

  // -------------------------------------------------------------------------
  //  scroll — GA4 default thresholds (25 / 50 / 75 / 90 %)
  //  --------------------------------------------------------
  //  Fires once per threshold per page load. Each event carries a
  //  `percent_scrolled` integer that matches GA4's built-in naming so
  //  Google Analytics picks it up automatically when wired through GTM.
  //  Also records `time_to_scroll_ms` (ms since page load) so engagement
  //  funnels can correlate depth with dwell time.
  // -------------------------------------------------------------------------
  function bindScrollDepth() {
    var thresholds = [25, 50, 75, 90];
    var fired = {};
    var startedAt = Date.now();
    var ticking = false;

    function check() {
      ticking = false;
      var doc = document.documentElement;
      var body = document.body;
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var viewport = window.innerHeight || doc.clientHeight || 0;
      var docHeight = Math.max(
        body.scrollHeight, doc.scrollHeight,
        body.offsetHeight, doc.offsetHeight,
        body.clientHeight, doc.clientHeight
      );
      var scrollable = docHeight - viewport;
      if (scrollable <= 0) return; // page shorter than viewport — nothing to scroll
      var pct = Math.round((scrollTop / scrollable) * 100);
      for (var i = 0; i < thresholds.length; i++) {
        var t = thresholds[i];
        if (pct >= t && !fired[t]) {
          fired[t] = true;
          push('scroll', {
            percent_scrolled: t,
            time_to_scroll_ms: Date.now() - startedAt,
            page_type: pageType()
          });
        }
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Run once on init in case the page is loaded already scrolled
    // (e.g. anchor-link arrival or back/forward cache restoration).
    check();
  }

  // -------------------------------------------------------------------------
  //  CTA buttons (.dev-button) — captured for click-through measurement
  // -------------------------------------------------------------------------
  function bindCtaButtons() {
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!(t instanceof Element)) return;
      var btn = t.closest('.dev-button');
      if (!btn) return;
      // Skip if already covered by a more specific event above.
      if (btn.matches('[data-dev-quiz-open], [data-dev-cart-open]')) return;
      push('cta_click', {
        cta_text: (btn.textContent || '').trim(),
        cta_url: btn.getAttribute('href') || '',
        cta_variant: btn.className.indexOf('dev-button--primary') !== -1 ? 'primary' : 'secondary',
        location: contextLabelFor(btn)
      });
    });
  }

  // -------------------------------------------------------------------------
  //  Bootstrap
  // -------------------------------------------------------------------------
  function init() {
    log('initialised', { gtmId: cfg.gtmId, debug: debug, enabled: cfg.enabled !== false });
    wrapFetch();
    emitPageView();
    emitViewItem();
    observeCards();
    bindSelectItem();
    bindCartDrawerEvents();
    bindBeginCheckout();
    bindSearch();
    bindQuiz();
    bindForms();
    bindNavigation();
    bindVideos();
    bindScrollDepth();
    bindCtaButtons();
    // Warm the cart cache so view_cart / begin_checkout can fire synchronously
    // even on the very first interaction. Idle callback to avoid contending
    // with critical first-paint requests.
    var warm = function () { refreshCartCache(); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 2000 });
    else setTimeout(warm, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
